"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateUserLastLogin = void 0;
const axios_1 = __importDefault(require("axios"));
const SearchParameter_1 = require("../../../user/src/types/SearchParameter");
const useAuth_1 = __importDefault(require("./useAuth"));
const useGet_1 = __importDefault(require("./useGet"));
const internalAdminEndpoint = '/mlp-user-service/services/v1/internal-admin';
const dentalAuthServiceEndpoint = '/dpp-auth-service/users/search';
// If we implement paging we should move these to be function parameters
const pageNum = `0`;
const pageSize = `100`;
function useUserApi({ success, failure } = {}) {
    const { authHeader } = (0, useAuth_1.default)();
    const { get, data, isLoading, error } = (0, useGet_1.default)({ success, failure });
    function userSearch(searchText, searchParam, filterStatus, customer) {
        const args = searchText.split(',');
        const parsedFilterStatus = filterStatus !== 'all' ? filterStatus : null;
        const customerId = customer == null ? null : customer.customerId;
        let result;
        switch (searchParam) {
            case SearchParameter_1.UserSearchParameter.EMAIL:
                result = getUsersByEmailAddress(args[0], parsedFilterStatus, customerId);
                break;
            case SearchParameter_1.UserSearchParameter.USERNAME:
                result = getUsersByLoginId(args[0], parsedFilterStatus, customerId);
                break;
            default:
                if (args.length > 1) {
                    result = getUsersByLastNameAndFirstName(args[0], parsedFilterStatus, customerId, args[1].trim());
                }
                else {
                    result = getUsersByLastName(args[0], parsedFilterStatus, customerId);
                }
        }
        return result;
    }
    function getUsersByLastName(lastName, parsedFilterStatus, customerId) {
        return {
            MLP: axios_1.default.post(`${internalAdminEndpoint}?page=${pageNum}&size=${pageSize}`, {
                lastName: lastName,
                isActive: parsedFilterStatus,
                customerId: customerId,
            }, {
                headers: {
                    Authorization: authHeader,
                },
            }),
            DPP: axios_1.default.post(`${dentalAuthServiceEndpoint}?page=${pageNum}&size=${pageSize}`, {
                lastName: lastName,
            }, {
                headers: {
                    Authorization: authHeader,
                },
            }),
        };
    }
    function getUsersByLastNameAndFirstName(lastName, parsedFilterStatus, customerId, firstName) {
        return {
            MLP: axios_1.default.post(`${internalAdminEndpoint}?page=${pageNum}&size=${pageSize}`, {
                lastName: lastName,
                firstName: firstName,
                isActive: parsedFilterStatus,
                customerId: customerId,
            }, {
                headers: {
                    Authorization: authHeader,
                },
            }),
            DPP: axios_1.default.post(`${dentalAuthServiceEndpoint}?page=${pageNum}&size=${pageSize}`, {
                lastName: lastName,
                firstName: firstName,
            }, {
                headers: {
                    Authorization: authHeader,
                },
            }),
        };
    }
    function getUsersByEmailAddress(emailAddress, parsedFilterStatus, customerId) {
        return {
            MLP: axios_1.default.post(`${internalAdminEndpoint}?page=${pageNum}&size=${pageSize}`, {
                email: emailAddress,
                isActive: parsedFilterStatus,
                customerId: customerId,
            }, {
                headers: {
                    Authorization: authHeader,
                },
            }),
            DPP: axios_1.default.post(`${dentalAuthServiceEndpoint}?page=${pageNum}&size=${pageSize}`, {
                email: emailAddress,
            }, {
                headers: {
                    Authorization: authHeader,
                },
            }),
        };
    }
    function getUsersByLoginId(loginId, parsedFilterStatus, customerId) {
        return {
            MLP: axios_1.default.post(`${internalAdminEndpoint}?page=${pageNum}&size=${pageSize}`, {
                loginId: loginId,
                isActive: parsedFilterStatus,
                customerId: customerId,
            }, {
                headers: {
                    Authorization: authHeader,
                },
            }),
            DPP: axios_1.default.post(`${dentalAuthServiceEndpoint}?page=${pageNum}&size=${pageSize}`, {
                email: loginId,
            }, {
                headers: {
                    Authorization: authHeader,
                },
            }),
        };
    }
    function getAuditLog(loginId) {
        return get({
            url: `/mlp-user-service/services/v1/audit/${loginId}`,
        });
    }
    function getErAdminRoleEvents(loginId) {
        return get({
            url: `/mlp-user-service/services/v1/audit/${loginId}/er-admin-role-events`,
        });
    }
    function customerSearch(searchText, successOverride, failureOverride) {
        const args = searchText.split(',');
        return get({
            url: `/config-service/admin/customer/search/${args[0]}`,
            success: successOverride,
            failure: failureOverride,
        });
    }
    function userPatch(userLoginId, userChanges) {
        return axios_1.default.patch(`/mlp-user-service/services/v1/users/${userLoginId}`, userChanges, {
            headers: {
                Authorization: authHeader,
                'Content-Type': 'application/json',
            },
        });
    }
    function addInternalUser({ firstName, lastName, employeeId, emailAddress, phoneNumber }) {
        return axios_1.default.post(`/mlp-user-service/services/v1/users/internal-user`, {
            userMetadata: {
                firstName,
                lastName,
                employeeId,
                emailAddress,
                phoneNumber,
            },
        }, {
            headers: {
                Authorization: authHeader,
            },
        });
    }
    function getMyProfile(authHeaderOverride = '') {
        const options = authHeaderOverride && {
            headers: {
                Authorization: authHeaderOverride,
            },
        };
        return get({
            url: `/mlp-user-service/services/v1/users/my-profile`,
            options,
        });
    }
    function dentalUserPatch(userLoginId, userChanges) {
        const dentalUserEndpoint = `/dpp-auth-service/users/${userLoginId}`;
        return axios_1.default.patch(dentalUserEndpoint, userChanges, {
            headers: {
                Authorization: authHeader,
                'Content-Type': 'application/json',
            },
        });
    }
    function userRelationshipsSearch(hubUsername) {
        return axios_1.default.post(`/mlp-user-service/services/v1/users/user-relationships/search`, {
            hubUsername,
            relationshipType: 'COMPANY_CODE',
            active: true,
        }, {
            headers: {
                Authorization: authHeader,
            },
        });
    }
    function validateCompanyCode(companyCode) {
        return axios_1.default.get(`/mlp-user-service/services/v1/users/user-relationships/validate-code/${encodeURIComponent(companyCode)}`, {
            headers: {
                Authorization: authHeader,
            },
        });
    }
    function addCompanyCode(hubUsername, relationshipValue) {
        return axios_1.default.post(`/mlp-user-service/services/v1/users/user-relationships`, {
            hubUsername,
            relationshipType: 'COMPANY_CODE',
            relationshipValue,
        }, {
            headers: {
                Authorization: authHeader,
            },
        });
    }
    return {
        userSearch,
        addInternalUser,
        userPatch,
        customerSearch,
        getMyProfile,
        getAuditLog,
        getErAdminRoleEvents,
        dentalUserPatch,
        userRelationshipsSearch,
        validateCompanyCode,
        addCompanyCode,
        data,
        isLoading,
        error,
    };
}
const updateUserLastLogin = (authHeader, userId, requestBody) => {
    return axios_1.default.patch(encodeURI(`/mlp-user-service/services/v1/users/${userId}`), requestBody, {
        headers: {
            Authorization: authHeader,
            'Content-Type': 'application/json',
        },
    });
};
exports.updateUserLastLogin = updateUserLastLogin;
exports.default = useUserApi;
